import { useState, useEffect, useCallback } from 'react';
import {
  getAllOfficesForAdmin,
  getAllOfficesForOfficeManagerOrAdmin,
  getAllDashboardOffices,
  getOfficeInformationById,
  getOfficeById,
} from '../services/offices.service';
import Office from '../models/office';
import type DashboardOffice from '../../dashboard/models/dashboard-office';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import { dataCache, OFFICES_CACHE_KEY, OFFICES_MANAGER_CACHE_KEY } from '../../../core/utils/data-cache';

interface OfficeState {
  readonly initialized: boolean;
  readonly office?: Office;
  readonly error?: boolean;
}

interface OfficesState {
  readonly initialized: boolean;
  readonly offices?: Office[];
  readonly error?: boolean;
}

interface DashboardOfficesState {
  readonly initialized: boolean;
  readonly dashboardOffices?: DashboardOffice[];
  readonly error?: boolean;
}

export const useAllDashboardOffices = (pageNumber: number, pageSize: number): DashboardOfficesState => {
  const [state, setState] = useState<DashboardOfficesState>({
    initialized: false,
  });
  const keycloak = useKeycloak();
  useEffect(() => {
    const getDashboardOffices = async (): Promise<void> => {
      try {
        const response = await getAllDashboardOffices(keycloak, pageNumber, pageSize);
        const dashboardOffices = response ?? [];
        setState({ initialized: true, dashboardOffices });
      } catch (error) {
        setState({ initialized: false, error: true });
      }
    };

    getDashboardOffices();
  }, [keycloak, pageNumber, pageSize]);

  return state;
};

export const useOfficeManagerOrAdminOffices = (): OfficesState => {
  // Seed from cache for instant rendering on subsequent visits (stale-while-revalidate).
  const [state, setState] = useState<OfficesState>(() => {
    const cached = dataCache.get<Office[]>(OFFICES_MANAGER_CACHE_KEY);
    return cached != null
      ? { initialized: true, offices: cached }
      : { initialized: false };
  });

  const keycloak = useKeycloak();

  useEffect(() => {
    // Always revalidate in the background so the list stays fresh even when
    // served from cache.  This mirrors the stale-while-revalidate pattern used
    // by useAdminOrAuthorOffices.
    const getOffices = async (): Promise<void> => {
      try {
        const offices = (await getAllOfficesForOfficeManagerOrAdmin(keycloak)) ?? [];
        dataCache.set(OFFICES_MANAGER_CACHE_KEY, offices);
        setState({ initialized: true, offices });
      } catch (error) {
        // Do not overwrite a previously successful result with an error state.
        setState((prev) => {
          if (prev.initialized) return prev;
          return { initialized: false, error: true };
        });
      }
    };

    getOffices();
  // Intentionally omit `state` from deps: we always want a background refresh
  // on mount regardless of whether the cache was warm.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak]);

  return state;
};

/**
 * Returns the list of offices that the current user owns or administers.
 *
 * Performance optimisation: the response is stored in an in-memory cache so
 * that navigating away from and back to "My Offices" renders data immediately
 * (stale-while-revalidate).  A background fetch always runs on mount to keep
 * the cache fresh.  The cache is automatically invalidated whenever an office
 * is created, updated or deleted (see `offices.service.ts`).
 *
 * @param version - Increment this value to force an explicit cache flush and
 *   re-fetch (e.g. when the admin presses "Refresh data").  Defaults to 0.
 */
export const useAdminOrAuthorOffices = (version = 0): OfficesState => {
  // Initialise from cache for instant rendering on subsequent visits.
  const [state, setState] = useState<OfficesState>(() => {
    const cached = dataCache.get<Office[]>(OFFICES_CACHE_KEY);
    return cached != null
      ? { initialized: true, offices: cached }
      : { initialized: false };
  });

  const keycloak = useKeycloak();

  useEffect(() => {
    let cancelled = false; // Prevent state updates after unmount.

    // When an explicit refresh is requested (version > 0), show the loading
    // indicator immediately so the user knows something is happening.
    if (version > 0) {
      setState({ initialized: false });
    }

    const getOffices = async (): Promise<void> => {
      try {
        const offices = (await getAllOfficesForAdmin(keycloak)) ?? [];
        if (!cancelled) {
          // Refresh the cache with the latest server response.
          dataCache.set(OFFICES_CACHE_KEY, offices);
          setState({ initialized: true, offices });
        }
      } catch (error) {
        // Only mark as errored when there is no cached data to fall back on.
        if (!cancelled && !dataCache.get(OFFICES_CACHE_KEY)) {
          setState({ initialized: false, error: true });
        }
      }
    };

    // Always revalidate in the background so the list stays up-to-date even
    // when cached data was served immediately.
    getOffices();

    return (): void => {
      cancelled = true;
    };
    // Re-run when the keycloak instance changes OR when an explicit refresh is
    // requested (version bump).
  }, [keycloak, version]);

  return state;
};

interface Props {
  readonly id: string | undefined;
}

export const useOfficeInformation = ({ id }: Props): OfficeState & { refresh: () => void } => {
  const [state, setState] = useState<OfficeState>({
    initialized: false,
  });
  const keycloak = useKeycloak();

  useEffect(() => {
    const getOffice = async (): Promise<void> => {
      try {
        let office;
        if (id === '' || !id) {
          office = new Office();
        } else {
          office = await getOfficeInformationById(id, keycloak);
        }

        setState({ initialized: true, office });
      } catch (error) {
        setState({ initialized: false, error: true });
      }
    };

    if (!state.initialized) {
      getOffice();
    }
  }, [setState, state.initialized, id, keycloak, state]);

  // Resets initialized so the effect above re-fetches the latest office data.
  const refresh = useCallback(() => setState((prev) => ({ ...prev, initialized: false })), []);

  return { ...state, refresh };
};

export const useOffice = ({ id }: Props): OfficeState => {
  const [state, setState] = useState<OfficeState>({
    initialized: false,
  });
  const keycloak = useKeycloak();

  useEffect(() => {
    const getOffice = async (): Promise<void> => {
      try {
        let office;
        if (id === '' || !id) {
          office = new Office();
        } else {
          office = await getOfficeById(id, keycloak);
        }

        setState({ initialized: true, office });
      } catch (error) {
        setState({ initialized: false, error: true });
      }
    };

    if (!state.initialized) {
      getOffice();
    }
  }, [setState, state.initialized, id, keycloak, state]);

  return state;
};
